import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetBalanceDto } from '@shared/api/contracts';
import type { WithdrawalAddressItem } from '@shared/api/contracts/security';
import {
  AUTO_WITHDRAWAL_PAIRS,
  getEnabledDirectDrafts,
  setDirectRuleEnabled,
  type AutoWithdrawalRuleDraft,
} from '@entities/withdrawal';
import type { AutoWithdrawalRuleFieldErrors } from '../../lib/validation';
import { getPairBalanceAmount } from '../../lib/pair-balance';
import { DirectRuleCard } from './direct-rule-card';

interface DirectRulesListProps {
  drafts: AutoWithdrawalRuleDraft[];
  assetBalances: AssetBalanceDto[];
  addresses?: WithdrawalAddressItem[];
  language?: string;
  disabled?: boolean;
  ruleErrors: Record<string, AutoWithdrawalRuleFieldErrors>;
  firstInvalidKey?: string;
  onDraftsChange: (drafts: AutoWithdrawalRuleDraft[]) => void;
  onReorder: (pairKey: string, direction: 'up' | 'down') => void;
}

const GROUP_LABELS: Record<string, string> = {
  TRC20: 'withdrawal.auto.groups.trc20',
  ERC20: 'withdrawal.auto.groups.erc20',
  BEP20: 'withdrawal.auto.groups.bep20',
  BTC: 'withdrawal.auto.groups.btc',
  LTC: 'withdrawal.auto.groups.ltc',
};

export const DirectRulesList = ({
  drafts,
  assetBalances,
  addresses = [],
  language = 'en',
  disabled = false,
  ruleErrors,
  firstInvalidKey,
  onDraftsChange,
  onReorder,
}: DirectRulesListProps) => {
  const { t } = useTranslation('cabinet');
  const invalidRef = useRef<HTMLDivElement | null>(null);
  const enabledDrafts = getEnabledDirectDrafts(drafts);
  const showPriorityControls = enabledDrafts.length > 1;

  const disabledByGroup = useMemo(() => {
    const groups = new Map<string, AutoWithdrawalRuleDraft[]>();
    AUTO_WITHDRAWAL_PAIRS.forEach((pair) => {
      const draft = drafts.find((item) => item.key === pair.key);
      if (!draft || draft.enabled) return;
      const list = groups.get(pair.group) ?? [];
      list.push(draft);
      groups.set(pair.group, list);
    });
    return groups;
  }, [drafts]);

  const updateDraft = (pairKey: string, patch: Partial<AutoWithdrawalRuleDraft>) => {
    onDraftsChange(
      drafts.map((draft) => (draft.key === pairKey ? { ...draft, ...patch } : draft)),
    );
  };

  const renderCard = (draft: AutoWithdrawalRuleDraft, priorityRank?: number) => {
    const enabledIndex = enabledDrafts.findIndex((item) => item.key === draft.key);

    return (
      <DirectRuleCard
        key={draft.key}
        draft={draft}
        pairBalance={getPairBalanceAmount(assetBalances, draft.assetCode, draft.networkCode)}
        disabled={disabled}
        priorityRank={priorityRank}
        showPriority={showPriorityControls}
        canMoveUp={enabledIndex > 0}
        canMoveDown={enabledIndex >= 0 && enabledIndex < enabledDrafts.length - 1}
        errors={ruleErrors[draft.key]}
        addresses={addresses}
        language={language}
        cardRef={draft.key === firstInvalidKey ? invalidRef : undefined}
        onEnabledChange={(enabled) => onDraftsChange(setDirectRuleEnabled(drafts, draft.key, enabled))}
        onAddressChange={(destinationAddress) => updateDraft(draft.key, { destinationAddress })}
        onAmountModeChange={(amountMode) =>
          updateDraft(draft.key, {
            amountMode,
            reserveAmount: amountMode === 'FULL_BALANCE' ? '' : draft.reserveAmount,
          })
        }
        onThresholdChange={(thresholdAmount) => updateDraft(draft.key, { thresholdAmount })}
        onReserveChange={(reserveAmount) => updateDraft(draft.key, { reserveAmount })}
        onMoveUp={() => onReorder(draft.key, 'up')}
        onMoveDown={() => onReorder(draft.key, 'down')}
      />
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{t('withdrawal.auto.direct.intro')}</p>
      {enabledDrafts.length > 0 && (
        <section className="space-y-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">{t('withdrawal.auto.direct.enabledSectionTitle')}</h3>
            {showPriorityControls && (
              <p className="text-xs text-muted-foreground">{t('withdrawal.auto.direct.priorityHint')}</p>
            )}
          </div>
          <div className="space-y-3">
            {enabledDrafts.map((draft, index) => renderCard(draft, index + 1))}
          </div>
        </section>
      )}

      {Array.from(disabledByGroup.entries()).map(([group, groupDrafts]) => (
        <section key={group} className="space-y-3">
          <h3 className="text-sm font-semibold">{t(GROUP_LABELS[group] ?? group)}</h3>
          <div className="space-y-3">{groupDrafts.map((draft) => renderCard(draft))}</div>
        </section>
      ))}
    </div>
  );
};
